import { useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';

/**
 * A line over time (ADR-0054): one figure, one line per source. Drawn to the
 * width it is given, so text stays text-sized; a pointer over it shows the
 * nearest day's values. No chart library: the panel draws only this.
 */

export interface ChartSeries {
  readonly key: string;
  readonly label: string;
  /** Oldest first, YYYY-MM-DD. */
  readonly points: readonly { readonly date: string; readonly value: number }[];
}

const HEIGHT = 220;
const PAD = { top: 12, right: 12, bottom: 26, left: 52 };
const DAY = 24 * 60 * 60_000;
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const OF_MONTH = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

const time = (date: string) => new Date(`${date}T00:00:00Z`).getTime();

/** Round steps for an axis: 1, 2 or 5 times a power of ten. */
function niceStep(span: number, count: number): number {
  const raw = span / Math.max(1, count);
  const power = 10 ** Math.floor(Math.log10(raw || 1));
  const scaled = raw / power;
  return (scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10) * power;
}

function dayLabel(at: number): string {
  const date = new Date(at);
  return `${date.getUTCDate()} ${OF_MONTH[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

export function LineChart({
  series,
  label,
  format = (value) => value.toLocaleString('ru-RU'),
  invert = false,
}: {
  readonly series: readonly ChartSeries[];
  /** What the chart shows, for screen readers. */
  readonly label: string;
  readonly format?: (value: number) => string;
  /** Lower is better — a search position: the axis runs downwards. */
  readonly invert?: boolean;
}): ReactNode {
  const box = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const element = box.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(280, Math.round(entry.contentRect.width)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const drawn = useMemo(() => series.filter((line) => line.points.length > 0), [series]);
  const all = drawn.flatMap((line) => line.points);
  // The box stays even when empty: its width is measured once, when it mounts.
  if (all.length === 0) return <div className="chart" ref={box} />;

  const times = all.map((point) => time(point.date));
  const from = Math.min(...times);
  const to = Math.max(...times, from + DAY);
  const values = all.map((point) => point.value);
  const low = invert ? Math.min(...values) : Math.min(0, ...values);
  const high = Math.max(...values, low + 1);
  const step = niceStep(high - low, 4);
  const bottom = Math.floor(low / step) * step;
  const top = Math.ceil(high / step) * step;
  const ticks: number[] = [];
  for (let value = bottom; value <= top + step / 2; value += step) ticks.push(value);

  const plotWidth = width - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const x = (at: number) => PAD.left + ((at - from) / (to - from)) * plotWidth;
  const y = (value: number) => {
    const share = (value - bottom) / (top - bottom || 1);
    return PAD.top + (invert ? share : 1 - share) * plotHeight;
  };

  // A label a month or so apart, as many as fit.
  const spanDays = (to - from) / DAY;
  const monthsApart = Math.max(1, Math.ceil(spanDays / 30 / Math.max(2, Math.floor(plotWidth / 90))));
  const xTicks: number[] = [];
  const start = new Date(from);
  for (let cursor = Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1); cursor <= to; ) {
    xTicks.push(cursor);
    const date = new Date(cursor);
    cursor = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthsApart, 1);
  }
  const xLabel = (at: number) => {
    const date = new Date(at);
    return date.getUTCMonth() === 0 || spanDays > 400 ? `${MONTHS[date.getUTCMonth()]} ${String(date.getUTCFullYear()).slice(2)}` : MONTHS[date.getUTCMonth()]!;
  };

  const dates = [...new Set(times)].sort((a, b) => a - b);
  const onMove = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const at = from + ((event.clientX - rect.left - PAD.left) / plotWidth) * (to - from);
    let nearest = dates[0]!;
    for (const date of dates) if (Math.abs(date - at) < Math.abs(nearest - at)) nearest = date;
    setHover(nearest);
  };
  const hovered = hover === null ? [] : drawn.map((line) => ({ line, point: line.points.find((point) => time(point.date) === hover) ?? null }));
  const last = drawn.map((line) => `${line.label}: ${format(line.points[line.points.length - 1]!.value)}`).join('; ');

  return (
    <div className="chart" ref={box}>
      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label={`${label}. ${dayLabel(from)} — ${dayLabel(to)}. Последнее: ${last}`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line className="chart-grid" x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} />
            <text className="chart-axis" x={PAD.left - 8} y={y(tick)} textAnchor="end" dominantBaseline="middle">
              {format(tick)}
            </text>
          </g>
        ))}
        {xTicks.map((tick) => (
          <text key={tick} className="chart-axis" x={x(tick)} y={HEIGHT - 8} textAnchor="middle">
            {xLabel(tick)}
          </text>
        ))}
        {drawn.map((line, index) => (
          <g key={line.key} className={`chart-series chart-series-${(index % 3) + 1}`}>
            {line.points.length === 1 ? (
              <circle cx={x(time(line.points[0]!.date))} cy={y(line.points[0]!.value)} r={3.5} />
            ) : (
              <polyline fill="none" points={line.points.map((point) => `${x(time(point.date)).toFixed(1)},${y(point.value).toFixed(1)}`).join(' ')} />
            )}
          </g>
        ))}
        {hover !== null ? (
          <g>
            <line className="chart-cursor" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={HEIGHT - PAD.bottom} />
            {hovered.map(({ line, point }, index) =>
              point ? <circle key={line.key} className={`chart-dot chart-series-${(drawn.indexOf(line) % 3) + 1}`} cx={x(hover)} cy={y(point.value)} r={4} data-index={index} /> : null,
            )}
          </g>
        ) : null}
      </svg>
      {hover !== null ? (
        <div className="chart-tip" style={{ left: Math.min(Math.max(x(hover) + 12, 0), width - 170) }}>
          <strong>{dayLabel(hover)}</strong>
          {hovered.map(({ line, point }) =>
            point ? (
              <span key={line.key} className={`chart-tip-row chart-series-${(drawn.indexOf(line) % 3) + 1}`}>
                <span className="chart-swatch" aria-hidden="true" />
                {line.label}: {format(point.value)}
              </span>
            ) : null,
          )}
        </div>
      ) : null}
      {drawn.length > 1 ? (
        <div className="chart-legend">
          {drawn.map((line, index) => (
            <span key={line.key} className={`chart-tip-row chart-series-${(index % 3) + 1}`}>
              <span className="chart-swatch" aria-hidden="true" />
              {line.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
